import { useCallback, useRef } from 'react';
import type { CanvasState } from './useCanvasTransform';
import type { CanvasObject, Artboard } from '../types';
import { getClickedArtboard } from '../utils/artboardUtils';
import { hitTestImageAtPoint } from '../utils/hitTestImage';

interface UseImageDragDropProps {
  canvasRef: React.RefObject<HTMLCanvasElement>;
  getCanvasState: () => CanvasState;
  onDropImage: (file: File, e: React.DragEvent, mousePos: { x: number; y: number }) => void;
  onDragOverImage?: (imageId: string | null) => void;
  onDragOverImageHash?: (imageHash: string | null) => void;
  onDragOverArtboard?: (artboardId: string | null) => void;
  onDropTemplate?: (file: File, targetArtboardId: string | null) => void;
  getObjectsArray: () => CanvasObject[];
  getArtboardsMap: () => Map<string, Artboard>;
  getObjectsMap: () => Map<string, CanvasObject>;
}

/**
 * 이미지 드래그 앤 드롭 훅 (성능 최적화)
 * - throttle로 드래그 오버 이벤트 최적화
 * - viewport culling으로 히트테스트 최적화
 */
export function useImageDragDrop({
  canvasRef,
  getCanvasState,
  onDropImage,
  onDragOverImage,
  onDragOverImageHash,
  onDragOverArtboard,
  onDropTemplate,
  getObjectsArray,
  getArtboardsMap,
  getObjectsMap
}: UseImageDragDropProps) {
  const throttleRef = useRef<number | null>(null);
  const lastDragOverRef = useRef<{ x: number; y: number } | null>(null);

  /**
   * 월드 좌표로 변환
   */
  const toWorld = useCallback((clientX: number, clientY: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const cx = clientX - rect.left;
    const cy = clientY - rect.top;
    const { scale, offset } = getCanvasState();
    return {
      x: (cx - offset.x) / scale,
      y: (cy - offset.y) / scale
    };
  }, [canvasRef, getCanvasState]);

  /**
   * 히트테스트 (viewport culling 최적화, 아트보드 클리핑 및 클리핑 마스크 고려)
   */
  const hitTestImage = useCallback(
    (x: number, y: number): { id: string; hash: string | null } | null => {
      return hitTestImageAtPoint(x, y, {
        objects: getObjectsArray(),
        objectsMap: getObjectsMap(),
        artboardsMap: getArtboardsMap(),
        canvasState: getCanvasState(),
        canvas: canvasRef.current
      });
    },
    [getObjectsArray, getObjectsMap, getArtboardsMap, getCanvasState, canvasRef]
  );

  /**
   * 드래그 오버 핸들러 (throttle 최적화)
   */
  const handleDragOver = useCallback((e: React.DragEvent) => {
    const hasFiles = Array.from(e.dataTransfer.types || []).includes('Files');
    if (!hasFiles) return;
    e.preventDefault();

    const isCtrlPressed = e.ctrlKey || e.metaKey;
    const isAltPressed = e.altKey;

    // 파일 타입 확인 (ZIP 파일 감지)
    const items = Array.from(e.dataTransfer.items || []);
    const files = Array.from(e.dataTransfer.files || []);
    let hasZip = false;
    let hasImage = false;
    
    // ZIP 파일 체크 (item.getAsFile()이 null일 수 있으므로 item.type도 체크)
    items.forEach(item => {
      if (item.kind === 'file') {
        const file = item.getAsFile();
        const itemType = item.type;
        
        // item.getAsFile()이 있으면 파일명과 타입 모두 체크
        if (file) {
          if (file.name && file.name.toLowerCase().endsWith('.zip')) {
            hasZip = true;
          } else if (file.type === 'application/zip' || file.type === 'application/x-zip-compressed') {
            hasZip = true;
          } else if (file.type.startsWith('image/')) {
            hasImage = true;
          }
        }
        
        // item.getAsFile()이 null이어도 item.type으로 체크 가능
        if (!file && itemType) {
          if (itemType === 'application/zip' || itemType === 'application/x-zip-compressed') {
            hasZip = true;
          } else if (itemType.startsWith('image/')) {
            hasImage = true;
          }
        }
      }
    });
    
    // items가 비어있을 경우 files로 체크
    if (items.length === 0 && files.length > 0) {
      files.forEach(file => {
        if (file.name && file.name.toLowerCase().endsWith('.zip')) {
          hasZip = true;
        } else if (file.type === 'application/zip' || file.type === 'application/x-zip-compressed') {
          hasZip = true;
        } else if (file.type.startsWith('image/')) {
          hasImage = true;
        }
      });
    }

    // 혼합 파일: 거부 모드
    if (hasZip && hasImage) {
      if (canvasRef.current) {
        e.dataTransfer.dropEffect = 'none';
        canvasRef.current.style.cursor = 'not-allowed';
      }
      onDragOverImage?.(null);
      onDragOverImageHash?.(null);
      onDragOverArtboard?.(null);
      return;
    }

    if (canvasRef.current) {
      e.dataTransfer.dropEffect = 'copy';
      canvasRef.current.style.cursor = 'copy';
    }

    // ZIP 파일 + Alt 키: 아트보드 하이라이트 모드 (throttle 전에 우선 처리)
    if (hasZip && !hasImage && isAltPressed && canvasRef.current) {
      const mousePos = toWorld(e.clientX, e.clientY);
      
      // 마지막 위치와 거리가 가까우면 스킵 (성능 최적화)
      if (lastDragOverRef.current) {
        const dx = mousePos.x - lastDragOverRef.current.x;
        const dy = mousePos.y - lastDragOverRef.current.y;
        if (dx * dx + dy * dy < 100) { // 10px 이내
          return;
        }
      }
      lastDragOverRef.current = mousePos;
      
      // 마우스 위치의 아트보드 찾기
      const artboards = Array.from(getArtboardsMap().values());
      const hoveredArtboardId = getClickedArtboard(mousePos.x, mousePos.y, artboards);
      
      // 아트보드 하이라이트 콜백 호출 (주황색)
      if (onDragOverArtboard) {
        onDragOverArtboard(hoveredArtboardId);
      }
      
      // 이미지 하이라이트 해제
      onDragOverImage?.(null);
      onDragOverImageHash?.(null);
      
      e.dataTransfer.dropEffect = hoveredArtboardId ? 'copy' : 'none';
      if (canvasRef.current) {
        canvasRef.current.style.cursor = hoveredArtboardId ? 'copy' : 'not-allowed';
      }
      return;
    }

    // throttle: 100ms마다 한 번만 실행
    if (throttleRef.current) {
      return;
    }

    throttleRef.current = window.setTimeout(() => {
      throttleRef.current = null;
    }, 100);

    // 이미지 교체 모드 (ZIP 파일이 아닐 때만)
    // Alt만: 단일 이미지 교체, Ctrl+Alt: 동일 이미지 일괄 교체
    if (isAltPressed && !hasZip && canvasRef.current) {
      const mousePos = toWorld(e.clientX, e.clientY);
      
      // 마지막 위치와 거리가 가까우면 스킵 (성능 최적화)
      if (lastDragOverRef.current) {
        const dx = mousePos.x - lastDragOverRef.current.x;
        const dy = mousePos.y - lastDragOverRef.current.y;
        if (dx * dx + dy * dy < 100) { // 10px 이내
          return;
        }
      }
      lastDragOverRef.current = mousePos;

      const hit = hitTestImage(mousePos.x, mousePos.y);
      
      if (isCtrlPressed && isAltPressed && onDragOverImageHash) {
        // Ctrl+Alt: 동일 해시 전체 하이라이트
        onDragOverImage?.(null);
        onDragOverImageHash(hit?.hash || null);
      } else {
        // Alt만: 단일 하이라이트
        onDragOverImage?.(hit?.id || null);
        onDragOverImageHash?.(null);
      }
      
      // 아트보드 하이라이트 해제
      onDragOverArtboard?.(null);
    } else {
      // ZIP 파일이거나 Alt 키가 없을 때는 모든 하이라이트 해제
      onDragOverImage?.(null);
      onDragOverImageHash?.(null);
      // ZIP 파일이 아닐 때만 아트보드 하이라이트 해제 (ZIP 파일은 위에서 이미 처리됨)
      if (!hasZip) {
        onDragOverArtboard?.(null);
      }
    }
  }, [canvasRef, toWorld, hitTestImage, getArtboardsMap, onDragOverImage, onDragOverImageHash, onDragOverArtboard]);

  /**
   * 드롭 핸들러
   */
  const handleDrop = useCallback((e: React.DragEvent) => {
    const hasFiles = Array.from(e.dataTransfer.types || []).includes('Files');
    if (!hasFiles) return;
    e.preventDefault();

    if (canvasRef.current) {
      canvasRef.current.style.cursor = 'default';
    }

    onDragOverImage?.(null);
    onDragOverImageHash?.(null);
    onDragOverArtboard?.(null);

    const files = Array.from(e.dataTransfer.files);
    
    // 파일 타입 분류
    const zipFiles = files.filter(file => 
      file.type === 'application/zip' || file.name.endsWith('.zip')
    );
    const imageFiles = files.filter(file => 
      file.type.startsWith('image/')
    );

    // 혼합 파일 체크: ZIP과 이미지가 동시에 있으면 무시
    if (zipFiles.length > 0 && imageFiles.length > 0) {
      return;
    }

    // ZIP 파일 처리
    if (zipFiles.length > 0) {
      // 여러 개면 랜덤 선택
      const selectedZip = zipFiles.length === 1 
        ? zipFiles[0] 
        : zipFiles[Math.floor(Math.random() * zipFiles.length)];
      
      const isAltPressed = e.altKey;
      
      // Alt 키가 눌린 경우: 마우스 위치의 아트보드 ID 전달
      if (isAltPressed) {
        const mousePos = toWorld(e.clientX, e.clientY);
        const artboards = Array.from(getArtboardsMap().values());
        const targetArtboardId = getClickedArtboard(mousePos.x, mousePos.y, artboards);
        
        // 아트보드 밖에 드롭하면 무시
        if (!targetArtboardId) {
          return;
        }
        
        if (onDropTemplate) {
          onDropTemplate(selectedZip, targetArtboardId);
        }
      } else {
        // Alt 키 없음: 전체 불러오기
        if (onDropTemplate) {
          onDropTemplate(selectedZip, null);
        }
      }
      return;
    }

    // 이미지 파일 처리 (기존 로직)
    if (imageFiles.length > 0) {
      const mousePos = toWorld(e.clientX, e.clientY);
      const isAltPressed = e.altKey;
      
      // Alt 키가 눌렸을 때는 이미지 교체 모드: 아트보드 내 이미지인지 확인
      if (isAltPressed) {
        const hit = hitTestImage(mousePos.x, mousePos.y);
        // 아트보드 내 이미지가 아니면 교체하지 않음
        if (!hit) {
          return;
        }
      }
      
      onDropImage(imageFiles[0], e, mousePos);
    }
  }, [canvasRef, toWorld, hitTestImage, getArtboardsMap, onDropImage, onDropTemplate, onDragOverImage, onDragOverImageHash, onDragOverArtboard]);

  /**
   * 드래그 리브 핸들러 (커서 복원)
   */
  const handleDragLeave = useCallback(() => {
    if (canvasRef.current) {
      canvasRef.current.style.cursor = 'default';
    }
    onDragOverImage?.(null);
    onDragOverImageHash?.(null);
    onDragOverArtboard?.(null);
  }, [canvasRef, onDragOverImage, onDragOverImageHash, onDragOverArtboard]);

  return {
    handleDragOver,
    handleDrop,
    handleDragLeave
  };
}

